import { BaseService } from "../../core/baseService";
import { ApiResult } from "../../core/types";
import {
  CurrencySettingsDto,
  UpdateCurrencySettingsDto,
  UpdateExchangeRatesDto,
  CurrencyConversionDto,
  CurrencyConversionResultDto,
  FormatCurrencyDto,
  FormatCurrencyResultDto,
  CurrencyFormatInfo,
} from "../types/currency.types";

export class CurrencyService extends BaseService {
  async getSettings(): Promise<ApiResult<CurrencySettingsDto>> {
    return this.handleRequest<CurrencySettingsDto>(
      this.client.get("/api/admin/currency/settings"),
    );
  }

  async updateSettings(
    settings: UpdateCurrencySettingsDto,
  ): Promise<ApiResult<CurrencySettingsDto>> {
    return this.handleRequest<CurrencySettingsDto>(
      this.client.put("/api/admin/currency/settings", settings),
    );
  }

  async getExchangeRates(): Promise<ApiResult<Record<string, number>>> {
    return this.handleRequest<Record<string, number>>(
      this.client.get("/api/admin/currency/exchange-rates"),
    );
  }

  async updateExchangeRates(
    rates: UpdateExchangeRatesDto,
  ): Promise<ApiResult<Record<string, number>>> {
    const result = await this.handleRequest<CurrencySettingsDto>(
      this.client.put("/api/admin/currency/exchange-rates", rates),
    );
    // Backend returns full settings, extract just the exchange rates
    if (result.success) {
      return { success: true, data: result.data.exchangeRates };
    }
    return result as ApiResult<Record<string, number>>;
  }

  async getCurrencyFormats(): Promise<
    ApiResult<Record<string, CurrencyFormatInfo>>
  > {
    return this.handleRequest<Record<string, CurrencyFormatInfo>>(
      this.client.get("/api/admin/currency/formats"),
    );
  }

  async convertCurrency(
    conversion: CurrencyConversionDto,
  ): Promise<ApiResult<CurrencyConversionResultDto>> {
    return this.handleRequest<CurrencyConversionResultDto>(
      this.client.post("/api/admin/currency/convert", conversion),
    );
  }

  async formatCurrency(
    format: FormatCurrencyDto,
  ): Promise<ApiResult<FormatCurrencyResultDto>> {
    return this.handleRequest<FormatCurrencyResultDto>(
      this.client.post("/api/admin/currency/format-amount", format),
    );
  }

  async validateConfiguration(): Promise<
    ApiResult<{ isValid: boolean; errors: string[] }>
  > {
    return this.handleRequest<{ isValid: boolean; errors: string[] }>(
      this.client.get("/api/admin/currency/validate"),
    );
  }

  async migrateExistingPrices(): Promise<
    ApiResult<{ success: boolean; message: string }>
  > {
    return this.handleRequest<{ success: boolean; message: string }>(
      this.client.post("/api/admin/currency/migrate", {}),
    );
  }
}
